import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';

import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import { useCreateBusinessApiKey, useGetBusinessApiKeyNew } from '@/apis/business/setting/apiKeys/hook';
import { ApiKeyAccessibility } from '@/apis/business/setting/apiKeys/types';

type FormValues = {
  name: string;
};

function grantFullAccess(accessibility: ApiKeyAccessibility): ApiKeyAccessibility {
  const next: ApiKeyAccessibility = {};
  for (const resource of Object.keys(accessibility)) {
    next[resource] = { create: true, read: true, update: true, delete: true };
  }
  return next;
}

export default function ApiKeyForm() {
  const navigate = useNavigate();

  const { data: seedData, isLoading: isSeedLoading } = useGetBusinessApiKeyNew();
  const { mutate: createApiKey, isPending } = useCreateBusinessApiKey();

  const { handleSubmit, control } = useForm<FormValues>({
    defaultValues: { name: '' }
  });

  const hasAccessibility = Boolean(seedData?.data?.accessibility && Object.keys(seedData.data.accessibility).length);

  const onSubmit = (data: FormValues) => {
    const accessibility = grantFullAccess(seedData?.data?.accessibility ?? {});
    createApiKey(
      {
        api_key: {
          name: data.name,
          status: true,
          accessibility
        }
      },
      {
        onSuccess: () => navigate(-1)
      }
    );
  };

  return (
    <Drawer
      anchor='right'
      open
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 480 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        px={3}
        py={2}
        borderBottom='1px solid #DEDEDE'
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        flexShrink={0}
      >
        <Typography variant='h6' fontWeight={500}>
          Add Key
        </Typography>
        <IconButton onClick={() => navigate(-1)} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        flex={1}
        display='flex'
        flexDirection='column'
        minHeight={0}
      >
        <Box flex={1} overflow='auto' p={3}>
          <Input name='name' label='Name' control={control} fullWidth required disabled={isSeedLoading} />
        </Box>

        <Box
          px={3}
          py={2}
          borderTop='1px solid #DEDEDE'
          display='flex'
          justifyContent='flex-end'
          gap={1.5}
          flexShrink={0}
        >
          <Button variant='outlined' onClick={() => navigate(-1)} sx={{ minHeight: 38 }}>
            Cancel
          </Button>
          <Button
            variant='contained'
            type='submit'
            isPending={isPending}
            disabled={!hasAccessibility || isSeedLoading}
            sx={{ minHeight: 38 }}
          >
            Create
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
